import QRCode from 'qrcode.react';
import { useReduxState } from 'hooks';
import { Alert, Avatar, Badge } from 'components/common';
import { Text } from 'components/common/form';
import { TwoFactorApps } from '.';

interface Props {
  isRequired?: boolean;
  secret: string;
  userEmail: string;
}

const TwoFactorSetup = ({ isRequired, secret, userEmail }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const label = `${serverConfig.hostname}:${userEmail}`;
  const issuer = serverConfig.siteTitle; // note: we do not want to translate this

  return (
    <div className="setup">
      {isRequired ? (
        <div className="required">
          <Alert text={messages.authTwoFactorRequired} type="warning-light" />
        </div>
      ) : null}

      <div className="section">
        <div className="title">
          <Avatar
            backgroundColor={theme.lightGreyDark}
            fontSize="1.3rem"
            padding="4px 9px"
            style={{
              borderRadius: 25,
              height: 25,
              width: 'auto'
            }}
          >
            {'1 / 3'}
          </Avatar>

          <div className="title-text">
            {messages.authTwoFactorSetupOneTitle}
          </div>
        </div>

        <p className="paragraph">{messages.authTwoFactorSetupOneInfo}</p>

        <TwoFactorApps />
      </div>

      <div className="section">
        <div className="title">
          <Avatar
            backgroundColor={theme.lightGreyDark}
            fontSize="1.3rem"
            padding="4px 9px"
            style={{
              borderRadius: 25,
              height: 25,
              width: 'auto'
            }}
          >
            {'2 / 3'}
          </Avatar>

          <div className="title-text">
            {messages.authTwoFactorSetupTwoTitle}
          </div>
        </div>

        <p className="paragraph">
          {`${messages.commonSelect} `}
          <code>{'+'}</code>
          {` ${messages.commonOr} `}
          <code>{messages.authTwoFactorAddAccount}</code>
          {` ${messages.authTwoFactorSetupTwoInfo}`}
        </p>

        <div className="code-qr">
          <QRCode
            renderAs="svg"
            size={128}
            value={`otpauth://totp/${label}?secret=${secret}&issuer=${issuer}`}
          />
        </div>

        <p className="paragraph">{messages.authTwoFactorSetupTwoManual}</p>

        <div className="code-manual">
          <Badge category="dark" isRounded={false} text={secret} />
        </div>
      </div>

      <div className="section">
        <div className="title">
          <Avatar
            backgroundColor={theme.lightGreyDark}
            fontSize="1.3rem"
            padding="4px 9px"
            style={{
              borderRadius: 25,
              height: 25,
              width: 'auto'
            }}
          >
            {'3 / 3'}
          </Avatar>
          <div className="title-text">
            {messages.authTwoFactorSetupThreeTitle}
          </div>
        </div>

        <div className="verify-input">
          <Text
            autoComplete="one-time-code"
            field="verificationCode"
            id="two-factor-verification-code"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={messages.commonEnterCode}
            required={true}
            validateOnChange={true}
          />
        </div>
      </div>

      <style jsx>{`
        .setup {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
        }
        .section {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .section:not(:first-child) {
          margin-top: 20px;
        }
        .title {
          align-items: center;
          display: flex;
        }
        .title-text {
          color: ${theme.textColor};
          font-weight: 600;
          margin-left: 8px;
        }
        .paragraph {
          margin: 10px 0;
        }
        .paragraph code {
          background-color: ${theme.lightGreyDark};
          border-radius: 3px;
          font-family: inherit;
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0 3px;
          padding: 0px 3px 1px;
        }
        .code-qr {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          display: inline-block;
          margin: 15px 0 10px;
          padding: 10px 10px 5px;
        }
        .verify-input {
          margin-top: 10px;
          max-width: 120px;
        }
      `}</style>
    </div>
  );
};

export default TwoFactorSetup;
